import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SecuritySection = ({ email }) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength += 25;
    if (/\d/?.test(password)) strength += 25;
    if (/[^a-zA-Z\d]/?.test(password)) strength += 25;
    return strength;
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (field === 'newPassword') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const handleChangePassword = () => {
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }
    if (passwordStrength < 75) {
      alert('La contraseña debe ser más segura');
      return;
    }
    // Implement password change logic
    alert('Contraseña actualizada exitosamente');
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordStrength(0);
  };

  const getStrengthColor = () => {
    if (passwordStrength < 50) return 'var(--color-destructive)';
    if (passwordStrength < 75) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 50) return 'Débil';
    if (passwordStrength < 75) return 'Media';
    return 'Fuerte';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/10">
          <Icon name="Lock" size={24} color="var(--color-warning)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Seguridad de la Cuenta</h2>
          <p className="text-sm text-muted-foreground">Gestiona tu contraseña y seguridad</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Current Email */}
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Correo Electrónico</p>
              <p className="text-sm text-muted-foreground">{email}</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-success/10 rounded-full">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <span className="text-xs font-semibold text-success">Verificado</span>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Cambiar Contraseña</h3>
          
          {/* Current Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Contraseña Actual *
            </label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordData?.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e?.target?.value)}
                placeholder="Ingresa tu contraseña actual"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <Icon
                  name={showCurrentPassword ? 'EyeOff' : 'Eye'}
                  size={20}
                  color="var(--color-muted-foreground)"
                />
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Nueva Contraseña *
            </label>
            <div className="relative">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={passwordData?.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e?.target?.value)}
                placeholder="Ingresa tu nueva contraseña"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <Icon
                  name={showNewPassword ? 'EyeOff' : 'Eye'}
                  size={20}
                  color="var(--color-muted-foreground)"
                />
              </button>
            </div>
            {passwordData?.newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Seguridad:</span>
                  <span className="text-xs font-semibold" style={{ color: getStrengthColor() }}>
                    {getStrengthLabel()}
                  </span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-300"
                    style={{
                      width: `${passwordStrength}%`,
                      backgroundColor: getStrengthColor()
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Confirmar Nueva Contraseña *
            </label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordData?.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e?.target?.value)}
                placeholder="Confirma tu nueva contraseña"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <Icon
                  name={showConfirmPassword ? 'EyeOff' : 'Eye'}
                  size={20}
                  color="var(--color-muted-foreground)"
                />
              </button>
            </div>
          </div>

          <Button
            variant="default"
            onClick={handleChangePassword}
            iconName="Shield"
            iconPosition="left"
            disabled={!passwordData?.currentPassword || !passwordData?.newPassword || !passwordData?.confirmPassword}
          >
            Actualizar Contraseña
          </Button>
        </div>

        {/* Security Tips */}
        <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Consejos de Seguridad</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Usa al menos 8 caracteres con mayúsculas, minúsculas y números</li>
                <li>• No compartas tu contraseña con nadie</li>
                <li>• Cambia tu contraseña regularmente</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;